import { EntitlementStatementArchiveCommand } from '@dreryk/egabinet-patient-frontend-api';

import { useCqrsCommand } from '@dreryk/command-query-react-common';
import { FormContextValues, useFormController } from '@dreryk/react-common';
import {
   Modal,
   ModalCancelButton,
   ModalCloseButton,
   ModalConfirmButton,
   ModalWidth,
   Textarea,
} from '@dreryk/react-components';

import { entitlementStatementsTranslationKeys } from '../../../translations';

import { ActionsCellProps } from './ActionsCell';

type EntitlementStatementArchiveFormBean = {
   archivingReason: string | null;
};

type EntitlementStatementArchiveFormController = FormContextValues<EntitlementStatementArchiveFormBean>;

type EntitlementStatementArchiveFormRenderer = (
   renderProps: Pick<EntitlementStatementArchiveFormController, 'handleSubmit' | 'formState'>
) => JSX.Element;

type EntitlementStatementArchiveModalConfirmButtonProps = Pick<
   EntitlementStatementArchiveFormController,
   'handleSubmit'
> &
   ActionsCellProps;

export const EntitlementStatementArchiveModalConfirmButton = (
   props: EntitlementStatementArchiveModalConfirmButtonProps
): JSX.Element => {
   const { executeAsync: archiveEntitlementStatement } = useCqrsCommand<void, EntitlementStatementArchiveFormBean>(
      (parameters) => {
         const command = new EntitlementStatementArchiveCommand();
         command.archivingReason = parameters.archivingReason!;
         command.id = props.id;
         return command;
      },
      {
         onSuccess: props.onArchive,
      }
   );

   return (
      <ModalConfirmButton
         label={entitlementStatementsTranslationKeys.archiveModal.delete}
         onClick={props.handleSubmit(archiveEntitlementStatement)}
      />
   );
};

type EntitlementStatementArchiveFormProps = {
   readonly children: EntitlementStatementArchiveFormRenderer;
};

const EntitlementStatementsArchiveForm = (props: EntitlementStatementArchiveFormProps): JSX.Element => {
   const { handleSubmit, register, formState } = useFormController<EntitlementStatementArchiveFormBean>({
      archivingReason: null,
   });

   return (
      <form noValidate>
         <Textarea
            testId={'reason'}
            label={entitlementStatementsTranslationKeys.archiveModal.reason}
            controller={register('archivingReason', { required: true })}
            placeholder={entitlementStatementsTranslationKeys.archiveModal.enterReason}
            maxLength={200}
            rows={4}
            maxRows={4}
         />
         {props.children({
            handleSubmit: handleSubmit,
            formState: formState,
         })}
      </form>
   );
};

type EntitlementStatementsArchiveModalProps = ActionsCellProps & {
   readonly onCancel: VoidFunction;
};

export const EntitlementStatementsArchiveModal = (props: EntitlementStatementsArchiveModalProps): JSX.Element => {
   return (
      <Modal
         testId={'archiveModal'}
         title={entitlementStatementsTranslationKeys.archiveModal.title}
         width={ModalWidth.NORMAL}
      >
         <EntitlementStatementsArchiveForm>
            {({ handleSubmit, formState }) => (
               <>
                  <EntitlementStatementArchiveModalConfirmButton
                     handleSubmit={handleSubmit}
                     onArchive={props.onArchive}
                     id={props.id}
                  />
                  <ModalCancelButton
                     onClick={props.onCancel}
                     shouldConfirmExit={{
                        enabled: formState.isDirty,
                        confirmationButtonLabel:
                           entitlementStatementsTranslationKeys.archiveModal.exitConfirmationModal.leave,
                     }}
                  />
                  <ModalCloseButton onClick={props.onCancel} />
               </>
            )}
         </EntitlementStatementsArchiveForm>
      </Modal>
   );
};
